import * as React from 'react';
import { isDefined } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { getSlope, getYIntercept } from '@react-financial-charts/interactive/lib/components/InteractiveStraightLine';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { EachFibChannel } from './Wrapper';
import type { EachFibChannelProps, TriangleDragData } from './Wrapper/EachFibChannel';
import type { ChartId, RenderNode } from '../../..';
import type { DragOverrideBase, FibChannelRenderNode, ParallelChannelRenderNode } from '../../../types/commontypes';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '../../../types/commontypes';
import { clampYToChart, ShapeType } from '../../..';
import { toFibChannelRFCProps, toParallelChannelRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { chartOrder } from '@/utils/chartConfig';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

export interface FibChannelProps {
  readonly enabled: boolean;
  readonly onStart: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  readonly onComplete: (e: React.MouseEvent, newChannels: RenderNode[], moreProps: MoreProps) => void;
  readonly onDragComplete: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly onSelect: (e: React.MouseEvent, interactives: RenderNode[], moreProps: MoreProps) => void;
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionOpacity?: number;
  readonly currentPositionRadius?: number;
  hoverText?: Partial<HoverTextConfig>;
  readonly channels: RenderNode[];
  readonly hoverId: string | null;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly chartId?: ChartId;
  readonly sharedState?: DrawingSharedState;
  readonly setSharedState?: React.Dispatch<React.SetStateAction<DrawingSharedState>>;
  readonly type?: ShapeType;
  readonly isAnyShapeEnabled?: boolean;
}

/** 드래그 중 임시 상태. uuid + TriangleDragData. */
interface FibChannelDragOverride extends DragOverrideBase, TriangleDragData {}

interface FibChannelState {
  channels?: RenderNode | null;
  override?: FibChannelDragOverride;
}

export class FibChannel extends React.Component<FibChannelProps, FibChannelState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!FibChannel._cachedDefaultProps) {
      FibChannel._cachedDefaultProps = {
        currentPositionStroke: '#1e53e6',
        currentPositionOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 4,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 18,
          bgWidth: 120,
          text: 'Click to select object',
        },
        channels: [],
      };
    }
    return FibChannel._cachedDefaultProps;
  }

  private terminate: () => void;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved: boolean = false;

  /**
   * RenderNode(또는 sharedState)에서 EachFibChannel에 필요한 데이터 필드만 추출한다.
   * spread 없이 필요한 필드만 명시적으로 반환하여 불필요한 RenderNode 필드가
   * EachFibChannel로 흘러들어가지 않게 한다.
   */
  private static toChannelRenderData(node: DrawingSharedState | Record<string, unknown>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const r = node as Record<string, unknown>;
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      uuid: (r.uuid as string | undefined) ?? '',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      startXY: (r.startXY as number[] | undefined) ?? [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      endXY: (r.endXY as number[] | undefined) ?? [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      dy: (r.dy as number | undefined) ?? 0,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      endXY2: r.endXY2 as number[] | undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      selected: (r.selected as boolean | undefined) ?? false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      locked: r.locked as boolean | undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      chartId: r.chartId as ChartId | undefined,
    };
  }

  public constructor(props: FibChannelProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType('channels').bind(this);

    this.state = {};
  }

  public render() {
    const {
      channels,
      currentPositionOpacity,
      currentPositionRadius = FibChannel.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      hoverText,
      onDoubleClick,
      sharedState,
      chartId,
      type,
      isAnyShapeEnabled,
    } = this.props;

    // FibChannel wrapper는 FIB_CHANNEL과 PARALLEL_CHANNEL 두 도형을 처리한다.
    // 도형 타입에 맞는 어댑터를 선택해야 storage-shape 필드 매핑이 정확하게 동작한다.
    const resolvedShapeType: ShapeType = type ?? ShapeType.FIB_CHANNEL;
    const isParallel = resolvedShapeType === ShapeType.PARALLEL_CHANNEL;
    const activeNode = getActiveTemplateNode(resolvedShapeType);
    const appearance = isParallel
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        toParallelChannelRFCProps(activeNode as ParallelChannelRenderNode)
      : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        toFibChannelRFCProps(activeNode as FibChannelRenderNode);

    const { override } = this.state;

    const overrideUuid = isDefined(override) ? override.uuid : null;

    const tempCondition = sharedState?.type === type && chartId === sharedState?.chartId;
    const tempData = tempCondition ? FibChannel.toChannelRenderData(sharedState ?? {}) : null;

    const tempChannel = tempData ? (
      <EachFibChannel
        interactive={false}
        uuid={tempData.uuid}
        startXY={tempData.startXY}
        endXY={tempData.endXY}
        dy={tempData.dy}
        endXY2={tempData.endXY2}
        selected={true}
        appearance={appearance}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        hoverText={hoverText as EachFibChannelProps['hoverText']}
        onDrag={this.handleDragChannel}
      />
    ) : null;

    return (
      <g>
        {channels.map((each, idx) => {
          const eachData = FibChannel.toChannelRenderData(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            each.uuid === overrideUuid ? { ...each, ...override } : (each as unknown as Record<string, unknown>),
          );
          // 노드의 실제 type을 확인하여 알맞은 어댑터로 변환한다.
          const eachAppearance =
            each.type === ShapeType.PARALLEL_CHANNEL
              ? toParallelChannelRFCProps(each)
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                toFibChannelRFCProps(each as FibChannelRenderNode);
          if (each.hidden) return null;
          return (
            <EachFibChannel
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              interactive={true}
              uuid={eachData.uuid}
              startXY={eachData.startXY}
              endXY={eachData.endXY}
              dy={eachData.dy}
              endXY2={eachData.endXY2}
              selected={eachData.selected}
              locked={eachData.locked}
              chartId={eachData.chartId}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              hoverText={hoverText as EachFibChannelProps['hoverText']}
              appearance={eachAppearance}
              onDrag={this.handleDragChannel}
              onDragComplete={this.handleDragChannelComplete}
              onSelect={this.handleSelect}
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              onHover={this.handleHover}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {tempChannel}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          // onClick={this.handleEnd}
          onMouseMove={this.handleDrawChannel}
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { channels, onSelect } = this.props;

    const newChannels = channels.map(channel => ({
      ...channel,
      selected: channel.uuid === selectedUuid,
    }));

    if (onSelect) {
      onSelect(e, newChannels, moreProps);
    }
  };

  private readonly handleDragChannel = (_: React.MouseEvent, uuid: string, newXYValue: TriangleDragData) => {
    const { override } = this.state;
    const { hoverId } = this.props;
    if (override?.uuid !== uuid || override?.uuid !== hoverId) {
      return;
    }
    this.setState({
      override: {
        uuid,
        ...newXYValue,
      },
    });
  };

  private readonly handleDragChannelComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    const { channels, onDragComplete, hoverId } = this.props;

    if (isDefined(override) && override.uuid === hoverId) {
      const { uuid, ...rest } = override;
      if (!isDefined(rest.startXY) || !isDefined(rest.endXY) || !isDefined(rest.endXY2)) {
        return;
      }
      const newChannels = channels.map(each =>
        each.uuid === uuid
          ? {
              ...each,
              ...rest,
              selected: true,
              updatedAt: Date.now(),
            }
          : {
              ...each,
              selected: false,
            },
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      onDragComplete?.(e, newChannels as RenderNode[], moreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleStart = (_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const { sharedState, enabled } = this.props;
    if (!enabled) return;
    if (!isDefined(sharedState) || !isDefined(sharedState?.startXY) || sharedState?.status !== 'started') {
      this.mouseMoved = false;
      const { onStart } = this.props;
      if (onStart !== undefined) {
        onStart(_, xy, moreProps);
      }
    } else {
      const shapesChartId = sharedState?.chartId;
      // Only do min/max clamp if sharedState exists
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xy[1]);
      this.handleEnd(_, [xy[0], yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, _xyValue: [number, number], moreProps: MoreProps) => {
    const { channels, sharedState, enabled, setSharedState } = this.props;
    const chartId = moreProps.chartId;
    if (!enabled) return;
    if (this.mouseMoved && isDefined(sharedState) && isDefined(sharedState.startXY)) {
      if (!isDefined(sharedState?.dy)) {
        setSharedState?.({
          ...sharedState,
          dy: 0,
        });
      } else {
        const targetChartId = sharedState?.chartId || chartId;
        const shapeUuid = crypto.randomUUID();
        const creationShapeType: ShapeType = this.props.type ?? ShapeType.FIB_CHANNEL;

        const lastProps = getActiveTemplateNode(creationShapeType) as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const defaults = defaultShapePropertyValues[creationShapeType] as Record<string, unknown>;
        const initial: Record<string, unknown> = { ...defaults, ...lastProps };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const newChannels = [
          ...channels.map(d => ({ ...d, selected: false })),
          {
            ...initial,
            ...sharedState,
            selected: true,
            type: creationShapeType,
            chartId: targetChartId,
            uuid: shapeUuid,
            hidden: false,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
        ] as unknown as RenderNode[];

        this.setState(
          {
            channels: newChannels[newChannels.length - 1],
          },
          () => {
            const { onComplete } = this.props;
            if (onComplete !== undefined) {
              onComplete(e, newChannels, moreProps);
            }
          },
        );
      }
    }
  };

  private readonly handleDrawChannel = (_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    const { setSharedState, sharedState, chartId } = this.props;
    if (isDefined(sharedState) && isDefined(sharedState.startXY)) {
      const startChartId = sharedState.chartId;
      this.mouseMoved = true;
      const pointerChartId = moreProps.currentCharts?.[0];

      // Only draw for shapes created on this chart
      if (chartId !== startChartId) return;

      const [x] = xyValue;
      let [, y] = xyValue;

      // Clamp Y if pointer is on a different chart
      y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);
      if (!isDefined(sharedState?.dy)) {
        setSharedState?.({
          ...sharedState,
          endXY: [x, y],
        });
      } else {
        const m = getSlope(sharedState.startXY, sharedState.endXY);
        const b = getYIntercept(m, sharedState.endXY);
        // @ts-expect-error

        const y1Value = m * sharedState.startXY[0] + b;
        const dy = y - y1Value;
        setSharedState?.({
          ...sharedState,
          endXY2: [x, y],
          dy: dy,
        });
      }
    }
  };
}
